// frontend/shared-ui/src/components/PurchaseOrderTemplate.jsx
import React from "react";
import { formatMoney } from "../utils/money";

/**
 * Printable Purchase Order template.
 * Mirrors InvoiceTemplate styling but has PO-specific fields:
 *   - issued-to supplier instead of billed-to customer
 *   - PO # / issue date / expected delivery
 *   - line items with receivedQuantity progress
 *
 * Props:
 *   po        — the purchase order object from /api/purchase-orders/:id
 *   supplier  — supplier object (may be embedded in po.supplier)
 *   store     — store identity from useStoreIdentity()
 */
export default function PurchaseOrderTemplate({ po, supplier, store }) {
  const cur = po?.currency || "USD";
  const sup = supplier || po?.supplier || {};

  const supplierAddress = [sup.address, sup.city, sup.country]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="fh-invoice" style={{ padding: "2rem", background: "#fff" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          borderBottom: "2px solid #0f172a",
          paddingBottom: "1rem",
          marginBottom: "1.5rem",
        }}
      >
        <div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>
            {store?.name || "FiberHome"}
          </div>
          {store?.address && (
            <div style={{ fontSize: 11.5, color: "#6b7280" }}>
              {store.address}
            </div>
          )}
          {[store?.phone, store?.email, store?.website].filter(Boolean).length >
            0 && (
            <div style={{ fontSize: 11.5, color: "#6b7280" }}>
              {[store.phone, store.email, store.website]
                .filter(Boolean)
                .join(" · ")}
            </div>
          )}
          {store?.taxId && (
            <div style={{ fontSize: 11.5, color: "#6b7280" }}>
              Tax ID: {store.taxId}
            </div>
          )}
        </div>
        <div style={{ textAlign: "right" }}>
          <div
            style={{
              fontSize: 22,
              fontWeight: 800,
              letterSpacing: "0.03em",
            }}
          >
            PURCHASE ORDER
          </div>
          <div
            style={{
              fontSize: 13,
              fontFamily: "monospace",
              marginTop: 4,
            }}
          >
            {po.code}
          </div>
          <div style={{ fontSize: 11.5, color: "#6b7280", marginTop: 4 }}>
            Issued {new Date(po.issuedAt || po.createdAt).toLocaleDateString()}
          </div>
          {po.expectedDelivery && (
            <div style={{ fontSize: 11.5, color: "#6b7280" }}>
              Expected {new Date(po.expectedDelivery).toLocaleDateString()}
            </div>
          )}
        </div>
      </div>

      {/* Parties */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "2rem",
          marginBottom: "1.5rem",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 10.5,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              fontWeight: 600,
              color: "#6b7280",
              marginBottom: 4,
            }}
          >
            Supplier
          </div>
          <div style={{ fontWeight: 600 }}>{sup.name || "—"}</div>
          {sup.contactName && <div>{sup.contactName}</div>}
          {sup.email && <div>{sup.email}</div>}
          {sup.phone && <div>{sup.phone}</div>}
          {supplierAddress && <div>{supplierAddress}</div>}
          {sup.taxId && <div>Tax ID: {sup.taxId}</div>}
        </div>
        <div style={{ textAlign: "right" }}>
          <div
            style={{
              fontSize: 10.5,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              fontWeight: 600,
              color: "#6b7280",
              marginBottom: 4,
            }}
          >
            Ship To
          </div>
          <div style={{ fontWeight: 600 }}>{store?.name || "FiberHome"}</div>
          {store?.address && <div>{store.address}</div>}
          {store?.city && <div>{store.city}</div>}
        </div>
      </div>

      {/* Items table */}
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: 12.5,
          marginBottom: "1.5rem",
        }}
      >
        <thead>
          <tr>
            <th
              style={{
                textAlign: "left",
                padding: "0.5rem",
                borderBottom: "1px solid #e5e7eb",
                fontSize: 10.5,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: "#6b7280",
                fontWeight: 600,
              }}
            >
              #
            </th>
            <th
              style={{
                textAlign: "left",
                padding: "0.5rem",
                borderBottom: "1px solid #e5e7eb",
                fontSize: 10.5,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: "#6b7280",
                fontWeight: 600,
              }}
            >
              Item
            </th>
            <th
              style={{
                textAlign: "right",
                padding: "0.5rem",
                borderBottom: "1px solid #e5e7eb",
                fontSize: 10.5,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: "#6b7280",
                fontWeight: 600,
              }}
            >
              Ordered
            </th>
            <th
              style={{
                textAlign: "right",
                padding: "0.5rem",
                borderBottom: "1px solid #e5e7eb",
                fontSize: 10.5,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: "#6b7280",
                fontWeight: 600,
              }}
            >
              Received
            </th>
            <th
              style={{
                textAlign: "right",
                padding: "0.5rem",
                borderBottom: "1px solid #e5e7eb",
                fontSize: 10.5,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: "#6b7280",
                fontWeight: 600,
              }}
            >
              Unit Price
            </th>
            <th
              style={{
                textAlign: "right",
                padding: "0.5rem",
                borderBottom: "1px solid #e5e7eb",
                fontSize: 10.5,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: "#6b7280",
                fontWeight: 600,
              }}
            >
              Total
            </th>
          </tr>
        </thead>
        <tbody>
          {(po.items || []).map((it, i) => (
            <tr key={it.id}>
              <td
                style={{ padding: "0.5rem", borderBottom: "1px solid #f3f4f6" }}
              >
                {i + 1}
              </td>
              <td
                style={{ padding: "0.5rem", borderBottom: "1px solid #f3f4f6" }}
              >
                <div style={{ fontWeight: 600 }}>
                  {it.productName || it.productSku || "—"}
                </div>
                {it.productSku && (
                  <div
                    style={{
                      fontSize: 11,
                      color: "#6b7280",
                      fontFamily: "monospace",
                    }}
                  >
                    {it.productSku}
                  </div>
                )}
                {it.notes && (
                  <div style={{ fontSize: 11, color: "#6b7280" }}>
                    {it.notes}
                  </div>
                )}
              </td>
              <td
                style={{
                  padding: "0.5rem",
                  textAlign: "right",
                  borderBottom: "1px solid #f3f4f6",
                }}
              >
                {it.quantity} {it.unit || ""}
              </td>
              <td
                style={{
                  padding: "0.5rem",
                  textAlign: "right",
                  borderBottom: "1px solid #f3f4f6",
                }}
              >
                {it.receivedQuantity || 0} {it.unit || ""}
              </td>
              <td
                style={{
                  padding: "0.5rem",
                  textAlign: "right",
                  borderBottom: "1px solid #f3f4f6",
                }}
              >
                {formatMoney(it.unitPrice, { symbol: cur })}
              </td>
              <td
                style={{
                  padding: "0.5rem",
                  textAlign: "right",
                  fontWeight: 600,
                  borderBottom: "1px solid #f3f4f6",
                }}
              >
                {formatMoney(Number(it.quantity) * Number(it.unitPrice || 0), {
                  symbol: cur,
                })}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td
              colSpan={5}
              style={{
                padding: "0.5rem",
                textAlign: "right",
                fontWeight: 600,
                borderTop: "1px solid #e5e7eb",
              }}
            >
              Subtotal
            </td>
            <td
              style={{
                padding: "0.5rem",
                textAlign: "right",
                fontWeight: 600,
                borderTop: "1px solid #e5e7eb",
              }}
            >
              {formatMoney(po.subtotal || 0, { symbol: cur })}
            </td>
          </tr>
          {Number(po.taxAmount || 0) > 0 && (
            <tr>
              <td colSpan={5} style={{ padding: "0.5rem", textAlign: "right" }}>
                Tax ({po.taxRate || 0}%)
              </td>
              <td style={{ padding: "0.5rem", textAlign: "right" }}>
                {formatMoney(po.taxAmount, { symbol: cur })}
              </td>
            </tr>
          )}
          {Number(po.shipping || 0) > 0 && (
            <tr>
              <td colSpan={5} style={{ padding: "0.5rem", textAlign: "right" }}>
                Shipping
              </td>
              <td style={{ padding: "0.5rem", textAlign: "right" }}>
                {formatMoney(po.shipping, { symbol: cur })}
              </td>
            </tr>
          )}
          <tr>
            <td
              colSpan={5}
              style={{
                padding: "0.5rem",
                textAlign: "right",
                fontWeight: 700,
                borderTop: "2px solid #0f172a",
              }}
            >
              Total
            </td>
            <td
              style={{
                padding: "0.5rem",
                textAlign: "right",
                fontWeight: 700,
                borderTop: "2px solid #0f172a",
                fontSize: 15,
              }}
            >
              {formatMoney(po.totalAmount || po.total || 0, { symbol: cur })}
            </td>
          </tr>
        </tfoot>
      </table>

      {/* Terms + footer */}
      {po.notes && (
        <div style={{ marginBottom: "1rem" }}>
          <div
            style={{
              fontSize: 10.5,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              fontWeight: 600,
              color: "#6b7280",
              marginBottom: 4,
            }}
          >
            Notes / Terms
          </div>
          <div style={{ fontSize: 12, whiteSpace: "pre-wrap" }}>{po.notes}</div>
        </div>
      )}

      <div
        style={{
          borderTop: "1px solid #e5e7eb",
          paddingTop: "1rem",
          textAlign: "center",
          fontSize: 11,
          color: "#6b7280",
        }}
      >
        Please confirm receipt of this purchase order and the expected delivery
        date. Contact {store?.name || "us"} with any questions.
      </div>
    </div>
  );
}
